import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface WebsiteNcofEducatorSubmission {
  id: string;
  name: string;
  email: string;
  phone: string;
  school: string;
  position: string;
  grade?: string | null;
  subject?: string | null;
  interested_in_tutoring: boolean;
  created_at?: string;
  is_read: boolean;
  read_at?: string | null;
}

export const useWebsiteNcofEducatorSubmissions = (enabled = true) => {
  return useQuery<WebsiteNcofEducatorSubmission[]>({
    queryKey: ['websiteNcofEducatorSubmissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('website_ncof_educator_submissions')
        .select(
          'id, name, email, phone, school, position, grade, subject, interested_in_tutoring, created_at, is_read, read_at'
        )
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data as WebsiteNcofEducatorSubmission[]) ?? [];
    },
    enabled,
    staleTime: 60_000,
  });
};

export type WebsiteNcofEducatorSubmissionUpdate = {
  name: string;
  email: string;
  phone: string;
  school: string;
  position: string;
  grade?: string | null;
  subject?: string | null;
  interested_in_tutoring: boolean;
};

export const useUpdateWebsiteNcofEducatorSubmissionReadStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isRead }: { id: string; isRead: boolean }) => {
      const updates = isRead
        ? { is_read: true, read_at: new Date().toISOString() }
        : { is_read: false, read_at: null };

      const { error } = await supabase
        .from('website_ncof_educator_submissions')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['websiteNcofEducatorSubmissions'] });
    },
  });
};

export const useUpdateWebsiteNcofEducatorSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: WebsiteNcofEducatorSubmissionUpdate;
    }) => {
      const { error } = await supabase
        .from('website_ncof_educator_submissions')
        .update({
          name: updates.name.trim(),
          email: updates.email.trim(),
          phone: updates.phone.trim(),
          school: updates.school.trim(),
          position: updates.position.trim(),
          grade: updates.grade?.trim() || null,
          subject: updates.subject?.trim() || null,
          interested_in_tutoring: updates.interested_in_tutoring,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['websiteNcofEducatorSubmissions'] });
    },
  });
};
